import { WebSocketServer, WebSocket } from "ws";
import { EventEmitter } from "events";
import { createRequire } from "module";
import type { ServerConfig } from "./types/server.js";
import { FileLogger, Logger, PanoramaLogger } from "./utils/logger.js";
import { ConnectionManager } from "./core/connectionManager.js";

// Create require function that works in both ESM and CommonJS
let requireFunc: NodeRequire;
try {
  // In ESM context
  requireFunc = createRequire(import.meta.url);
} catch {
  // In CommonJS context, use global require
  requireFunc = require;
}

const languageServerPath = requireFunc.resolve(
  "typescript-language-server/lib/cli.mjs"
);

export class WixCodeLSPServer extends EventEmitter {
  private wsServer?: WebSocketServer;
  private readonly logger: Logger;
  private readonly config: ServerConfig;
  private isRunning = false;
  private connectionManager: ConnectionManager;
  constructor(config: ServerConfig) {
    super();
    this.config = {
      host: "localhost",
      debug: false,
      ...config,
    };

    this.logger = new Logger(
      {
        targets: [
          new FileLogger(this.config.logFile),
          new PanoramaLogger(),
        ],
        debugEnabled: this.config.debug,
      }
    );

    this.connectionManager = new ConnectionManager(this.config, this.logger);
  }

  /**
   * Start the WebSocket LSP server
   */
  async start(): Promise<void> {
    if (this.isRunning) {
      throw new Error("Server is already running");
    }

    try {
      this.logger.info(
        `Starting Wix Code LSP Server on ${this.config.host}:${this.config.port}...`,
      );

      // Create WebSocket server
      this.wsServer = new WebSocketServer({
        host: this.config.host,
        port: this.config.port,
        perMessageDeflate: false, // Disable compression for better performance with LSP
      });

      // Set up WebSocket server event handlers
      this.setupWebSocketServerHandlers();

      this.isRunning = true;
      this.logger.info(
        `Wix Code LSP Server started successfully on ws://${this.config.host}:${this.config.port}`,
      );
      this.emit("server:started");
    } catch (error) {
      this.logger.error("Failed to start Wix Code LSP Server:", error);
      this.emit("server:error", error);
      throw error;
    }
  }

  /**
   * Stop the WebSocket LSP server
   */
  async stop(): Promise<void> {
    if (!this.isRunning) {
      return;
    }

    try {
      this.logger.info("Stopping Wix Code LSP Server...");

      // Close WebSocket server
      if (this.wsServer) {
        this.connectionManager.dispose();

        await new Promise<void>((resolve, reject) => {
          this.wsServer!.close((error) => {
            if (error) {
              reject(error);
            } else {
              resolve();
            }
          });
        });
      }

      this.isRunning = false;
      this.logger.info("Wix Code LSP Server stopped successfully");
      this.emit("server:stopped");
    } catch (error) {
      this.logger.error("Error stopping Wix Code LSP Server:", error);
      this.emit("server:error", error);
      throw error;
    }
  }

  /**
   * Check if server is running
   */
  getIsRunning(): boolean {
    return this.isRunning;
  }

  /**
   * Get server configuration
   */
  getConfig(): ServerConfig {
    return { ...this.config };
  }

  private setupWebSocketServerHandlers(): void {
    if (!this.wsServer) return;

    this.wsServer.on("connection", (websocket: WebSocket) => {
      this.handleNewConnection(websocket);
    });

    this.wsServer.on("error", (error) => {
      this.logger.error("WebSocket server error:", error);
      this.emit("server:error", error);
    });

    this.wsServer.on("close", () => {
      this.logger.info("WebSocket server closed");
      this.emit("server:closed");
    });
  }

  private async handleNewConnection(
    websocket: WebSocket,
  ): Promise<void> {
    try {
      this.connectionManager.createConnection(websocket, languageServerPath);
    } catch (error) {
      this.logger.error("Error handling new connection:", error);
      websocket.close(1011, "Server error");
    }
  }
}
